import { parse } from "@plussub/srt-vtt-parser";
import { Box, Collapse, Stack, styled, Typography, useTheme } from "@mui/material";
import {
  GetPlayerSetup,
  SetMediaData,
  UnsetInput,
  GetPlayerSeek,
  SetPlayingState,
  UnsetMediaData,
  GetPlayerInput,
  GetPlayerSetupData,
  ReactPlayerData,
  GetVolume,
  GetLanguages,
  SetAudioTracks,
  SetActiveLanguages,
  SetCaptions,
  GetFullScreen,
  GetPlaybackSpeed,
  GetPlayerPip,
  SetPlayerPip,
  SetVolume,
  SetVttThumbs,
  SetPlayerFullScreen,
  GetPlayingState,
  SetIsBuffering,
  GetIsBuffering,
  GetPlayerBackground,
  PlayPause,
  SetMarkInByFrame,
  SetMarkOutByFrame,
  SetCurrentFrame,
  SetFrameDifferential,
  GetRealCurrentFrame,
} from "../../../Redux/Reducers/redux-slice-react-player";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cacheService, mediaService, thumbnailService } from "../../../Core/Api/services";
import { defaultTimecode } from "../../../Utils/Timecode/ReactPlayer";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import { GetPlayerInfos, SetPlayerHeight, SetPlayerResponsive } from "../../../Redux/Reducers/redux-slice-layout";
import { PlayerControls } from "./player-controls";
import { PlayerProgress, ProgressBarType } from "./player-progress";
import { PlayerDefaultOverlay, AudioOverlay, CompleteOverlay, PlayerBufferingOverlay } from "./player-overlays";
import { PlayerTimeline } from "../MediaPlayerMarkers/media-player-timeline-markers";
import { GetCreatingOrEditingTimelineEvent } from "../../../Redux/Reducers/redux-slice-timeline";
import { PreviewContext } from "../../../Pages/admin/Inventory/InventoryAssetSingle/AssetSinglePreview/asset-single-preview";
import PlayerTimelinePlaceholder from "../MediaPlayerMarkers/media-player-timeline-placeholder";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { GetStreamingSmilByObjectIdResponse } from "../../../Core/Api/Types/get-streaming-smil-by-object-id-response";
import "../../../Theme/CSS/player.css";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";
import Hls from "hls.js";
import { FileConfig } from "react-player/file";
import imsc from "imsc";
import "../ReactPlayerWrapper/player.css";
import { OnProgressProps } from "react-player/base";
import { useIdle } from "@uidotdev/usehooks";
import useHover from "../../../Utils/Hooks/useHover";

const playbackRates = [0.5, 1, 1.5, 2, 4];

export type ReactPlayerWrapperProps = {
  children?: React.ReactNode;
  assetType?: ObjectType;
  shortcutEnable?: boolean;
  context?: PreviewContext;
  shareKey?: string;
};

interface ReactPlayerlaylist {
  file: string;
  tracks: ReactPlayerTrack[];
  image: string | undefined;
}

interface ReactPlayerTrack {
  file: string;
  kind: "captions" | "thumbnails";
  label: string | undefined;
}

declare global {
  interface Document {
    mozCancelFullScreen?: () => Promise<void>;
    msExitFullscreen?: () => Promise<void>;
    webkitExitFullscreen?: () => Promise<void>;
    mozFullScreenElement?: Element;
    msFullscreenElement?: Element;
    webkitFullscreenElement?: Element;
  }

  interface HTMLElement {
    msRequestFullscreen?: () => Promise<void>;
    mozRequestFullscreen?: () => Promise<void>;
    webkitEnterFullscreen?: () => Promise<void>;
    webkitRequestFullscreen?: () => Promise<void>;
  }

  interface HTMLVideoElement {
    webkitEnterFullscreen: () => Promise<void>;
  }
}

const isVtt = (src: string) => {
  return src.split(".")[src.split(".").length - 1] === "vtt";
};

const ReactPlayerWrapper: React.FC<ReactPlayerWrapperProps> = (props) => {
  const { t } = useTranslation();
  const _player = useRef<ReactPlayer | null>(null);
  const cuesRef = useRef<any[]>([]);
  const activeTrackIndexRef = useRef<number>(-1);

  const dispatch = useAppDispatch();
  const playerConf = useAppSelector(GetPlayerSetup);
  const playerData = useAppSelector(GetPlayerSetupData);
  const playerInput = useAppSelector(GetPlayerInput);
  const playerSeek = useAppSelector(GetPlayerSeek);
  const playerVolume = useAppSelector(GetVolume);
  const playerBackground = useAppSelector(GetPlayerBackground);
  const playerFullScreen = useAppSelector(GetFullScreen);
  const playerPip = useAppSelector(GetPlayerPip);
  const isPlaying = useAppSelector(GetPlayingState);
  const isBuffering = useAppSelector(GetIsBuffering);
  const playbackSpeed = useAppSelector(GetPlaybackSpeed);
  const currentFrame = useAppSelector(GetRealCurrentFrame);
  const isCreatingTimelineEvent = useAppSelector(GetCreatingOrEditingTimelineEvent);
  const { activeAudioTrackIndex, activeCaptionIndex } = useAppSelector(GetLanguages);
  const { containerHeight, aspectRatio, responsive, height: playerHeight } = useAppSelector(GetPlayerInfos);
  const [loading, setLoading] = useState(true);
  const [hasFirstPlayed, setHasFirstPlayed] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const imsDocRef = useRef<any>(undefined);
  const theme = useTheme();
  const { assetType = ObjectType.Video, context = PreviewContext.DistribEnlarged, shareKey } = props;
  const subtitlesList = useRef<any>(undefined);
  let captionSoloSet = false;

  const [jwPlaylist, setJwPlaylist] = useState<ReactPlayerlaylist[]>([
    {
      file: "",
      tracks: [],
      image: "",
    },
  ]);

  const [loadedKey, setLoadedKey] = useState<string | number>("");
  const [keyToLoad, setKeyToLoad] = useState<string | number | null>("");
  const [episodeToLoad, setEpisodeToLoad] = useState<number | null>(null);
  const [loadedEpisode, setLoadedEpisode] = useState<number | null>(null);
  const [isPlayerMounted, setIsPlayerMounted] = useState(false);
  const idle = useIdle(3000);
  const playerDiv = useRef<HTMLDivElement>(null);

  const hoveringPlayer = useHover(playerDiv);
  const background = useMemo(
    () => (playerBackground === "contrasted" ? `${theme.palette.background.default} !important` : "#000 !important"),
    [playerBackground, theme, theme.palette.mode]
  );

  const resetPlayerState = useCallback((key: string | number | null, episode?: number | null | undefined) => {
    setIsPlayerMounted(false);
    setJwPlaylist([
      {
        file: "",
        tracks: [],
        image: "",
      },
    ]);
    setLoading(true);
    setLoadedKey("");
    setLoadedEpisode(null);
    if (episode !== undefined) setEpisodeToLoad(episode);
    setKeyToLoad(key);
    dispatch(UnsetMediaData());
    dispatch(SetCurrentFrame(0));
    dispatch(SetPlayingState(false));
    dispatch(SetVolume(50));
    dispatch(SetCaptions([]));
    dispatch(SetActiveLanguages({ activeCaptionIndex: null, activeAudioTrackIndex: null }));
    dispatch(SetCaptions([]));
    dispatch(SetAudioTracks([]));
    dispatch(SetIsBuffering(false));
    setHasFirstPlayed(false);
  }, []);

  useEffect(() => {
    if (keyToLoad !== playerInput.objectId && keyToLoad !== playerInput.shareMediaKey)
      setKeyToLoad(playerInput.objectId ?? playerInput.shareMediaKey);
    if (playerInput.episode !== episodeToLoad) setEpisodeToLoad(playerInput.episode);
    if (!keyToLoad || keyToLoad === "") return;
    if (keyToLoad === playerInput.objectId && (!playerInput.episode || playerInput.episode === episodeToLoad)) {
      loadMediaDataSmil(keyToLoad, playerInput.episode);
    } else if (keyToLoad === playerInput.shareMediaKey) {
      loadMediaDataShareKey(keyToLoad);
    }
  }, [keyToLoad, episodeToLoad]);

  const processSetup = useCallback(
    (mediaData: any, key: string | number, success: boolean | null, episode?: number | null | undefined) => {
      dispatch(SetMediaData(mediaData));
      setLoading(false);
      setLoadedKey(key);
      if (episode) setLoadedEpisode(episode);

      if (success) {
        // Set default markers positions
        dispatch(SetMarkInByFrame(0.1 * mediaData.duration * mediaData.frameRate));
        dispatch(SetMarkOutByFrame(0.2 * mediaData.duration * mediaData.frameRate));

        playerSetup(mediaData);
      }
    },
    []
  );

  const loadMediaDataSmil = useCallback((objectId: number, episode: number | null) => {
    mediaService
      .GetStreamingSmilByObjectId(objectId, episode)
      .then(function (data) {
        let url = getPosterUrl(data);
        // Total playback duration
        const frameRate = data.frameRate === 23.98 ? 23.976 : data.frameRate!;
        // Set media data related to streamed object
        let mediaData: ReactPlayerData = {
          frameInSeconds: data.frameInSeconds!,
          frameRate: frameRate,
          smilFullUrl: data.smilFullUrl,
          captions: data.captions ?? [],
          image: url,
          thumbs: data.thumbs,
          message: data.message || undefined,
          objectId: data.objectId!,
          objectType: data.objectType!,
          objectClass: data.objectClass!,
          objectLayer: data.objectLayer!,
          timecodeIn: data.timecodeIn ?? defaultTimecode,
          totalFrameCount: data.durationInFrames!,
          durationInSeconds: data.durationInSeconds!,
          // this will be setup later when Player launches
          frameDifferential: 1,
          parentId: data.parentId!,
          videoStream: data.videoStream,
        };

        processSetup(mediaData, objectId, data.success, episode);
      })
      .catch((error) => {
        dispatch(
          SetMediaData({
            frameRate: 0,
            totalFrameCount: 0,
            durationInSeconds: 0,
            frameDifferential: 1,
            smilFullUrl: null,
            captions: [],
            message: error.message,
            thumbs: false,
            objectClass: ObjectClass.Raw,
            objectType: ObjectType.Unknown,
            videoStream: true,
          })
        );
        setLoading(false);
      });
  }, []);

  const loadMediaDataShareKey = useCallback((shareMediaKey: string) => {
    mediaService
      .GetStreamingSmilByShareMediaKey(shareMediaKey)
      .then(function (data) {
        // Total playback duration
        let url = getPosterUrl(data);

        const frameRate = data.frameRate === 23.98 ? 23.976 : data.frameRate!;

        // Set media data related to share
        let mediaData: ReactPlayerData = {
          frameInSeconds: data.frameInSeconds!,
          frameRate: frameRate,
          smilFullUrl: data.smilFullUrl,
          captions: data.captions! || [],
          thumbs: data.thumbs,
          message: data.message ?? undefined,
          image: url,
          objectId: data.objectId!,
          objectType: data.objectType!,
          objectClass: data.objectClass!,
          objectLayer: data.objectLayer!,
          timecodeIn: data.timecodeIn ?? defaultTimecode,
          totalFrameCount: data.durationInFrames!,
          durationInSeconds: data.durationInSeconds!,
          frameDifferential: 1,
          parentId: data.parentId!,
          videoStream: data.videoStream,
        };
        processSetup(mediaData, shareMediaKey, data.success);
      })
      .catch((error) => {
        setLoading(false);
      });
  }, []);

  const handlePlayPause = useCallback(() => {
    const htmlVideo = document.querySelectorAll("video");
    if (htmlVideo.length === 1 && htmlVideo[0].tagName === "VIDEO") {
      const video = htmlVideo[0] as HTMLVideoElement;
      if (video.paused || video.played.length === 0) {
        var playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              playerDiv?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            })
            .catch((error) => {
              console.log("Autoplay prevented or failed", error);
              setTimeout(() => {
                var retryPromise = video.play();
                retryPromise
                  .then(() => {
                    playerDiv?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                  })
                  .catch((error) => {
                    console.log("Retrying autoplay failed again", error);
                  });
              }, 500);
            });
        }
      } else {
        video.pause();
      }
    }
  }, []);

  const getVttThumbs = useCallback(async (url: string) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
      }

      const text = await response.text();
      const { entries } = parse(text);
      dispatch(SetVttThumbs(entries));
    } catch (error) {
      console.error(error);
    }
  }, []);

  const getPosterUrl = (data: GetStreamingSmilByObjectIdResponse) => {
    let url = "";
    // -1 returns the default thumbnail

    if (shareKey) {
      url = thumbnailService.GetSharePosterUrl(ThumbnailSize.xxlarge, shareKey);
    } else {
      if (data.objectClass === ObjectClass.Drive)
        url = thumbnailService.GetObjectPosterUrl(data.objectId ?? -1, ThumbnailSize.xxlarge);
      else if (data.objectLayer === ObjectLayer.Content || data.objectLayer === ObjectLayer.Title)
        url = thumbnailService.GetObjectPosterUrl(data.objectId ?? -1, ThumbnailSize.xxlarge);
      else if (data.objectLayer === ObjectLayer.Asset)
        url = thumbnailService.GetObjectPosterUrl(data.titleId ?? data.objectId, ThumbnailSize.xxlarge);
    }

    return url;
  };

  const initTrack = useCallback((text, activeCaptionIndex: number) => {
    const video = document.querySelector("video");
    if (!video) return;
    for (let i = 0; i < video.textTracks.length; i++) {
      video.textTracks[i].mode = i === activeCaptionIndex ? "showing" : "hidden";
    }
    const tracks = video.textTracks;
    const track = tracks[activeCaptionIndex];
    imsDocRef.current = imsc.fromXML(text);
    const timeEvents = imsDocRef.current.getMediaTimeEvents();
    for (let i = 0; i < timeEvents.length; i++) {
      let myCue;
      if (i < timeEvents.length - 1) {
        myCue = new VTTCue(timeEvents[i], timeEvents[i + 1], "");
      } else {
        myCue = new VTTCue(timeEvents[i], video.duration, "");
      }
      myCue.addEventListener("enter", function () {
        clearSubFromScreen();
        const myIsd = imsc.generateISD(imsDocRef.current, this.startTime);
        imsc.renderHTML(myIsd, subtitlesList.current);
      });
      myCue.addEventListener("exit", function () {
        clearSubFromScreen();
      });
      const r = track.addCue(myCue);
      cuesRef.current = [...cuesRef.current, myCue];
      activeTrackIndexRef.current = activeCaptionIndex;
      track.mode = "hidden";
    }
  }, []);

  const clearSubFromScreen = useCallback(() => {
    const subtitleActive = subtitlesList.current?.getElementsByTagName("div")[0];
    if (subtitleActive) {
      subtitlesList.current?.removeChild(subtitleActive);
    }
  }, []);

  const handleLeavePip = useCallback(() => {
    dispatch(SetPlayerPip(false));
  }, []);

  const handleEnablePip = useCallback(() => {
    dispatch(SetPlayerPip(true));
  }, []);

  // Needed for iOS Safari specific listeners
  const leaveHTMLVideoFullScreen = useCallback(() => {
    dispatch(SetPlayerFullScreen(false));
  }, []);

  const playerSetup = useCallback((playerData: ReactPlayerData) => {
    // Check for subtitles caption files
    let tracks: any[] = [];
    let labels: any[] = [];

    if (playerData.captions && playerData.captions.length > 0) {
      for (let i = 0; i < playerData.captions.length; i++) {
        if (labels.indexOf(playerData.captions[i].label) === -1) {
          tracks.push({
            file: playerData.captions[i].url,
            kind: "captions",
            label: playerData.captions[i]?.label,
          });

          labels.push(playerData.captions[i].label);
        }
      }
    }

    // Check for on-the-fly VTT thumbs
    if (playerData.thumbs && playerData.objectId) {
      let vttUrl = thumbnailService.GetRollThumbsVttTrackByObjectId(
        playerData.objectId,
        cacheService.GetAuthKey() ?? ""
      );

      const entries = getVttThumbs(vttUrl);

      tracks.push({
        file: vttUrl,
        kind: "thumbnails",
      });
    }
    dispatch(SetCaptions(tracks.map((x) => ({ url: x.file, label: x.label }))));
    // Set playlist (and add background thumbnail if available)
    if (playerData.smilFullUrl) {
      setJwPlaylist([
        {
          file: playerData.smilFullUrl,
          tracks: tracks,
          image: "",
        },
      ]);
    }
  }, []);

  const onBuffer = useCallback(() => {
    dispatch(SetIsBuffering(true));
  }, []);

  const onBufferEnd = useCallback(() => {
    dispatch(SetIsBuffering(false));
  }, []);

  const onProgress = useCallback((state: OnProgressProps) => {
    if (!("requestVideoFrameCallback" in HTMLVideoElement.prototype)) {
      dispatch(SetCurrentFrame(state.playedSeconds * playerData.frameRate));
    }
  }, []);

  const onPlay = useCallback(() => {
    dispatch(SetPlayingState(true));
    setHasFirstPlayed(true);
    setShowComplete(false);

    // Set single caption on by default
    if (
      jwPlaylist &&
      jwPlaylist.length === 1 &&
      jwPlaylist[0].tracks.filter((x) => x.kind === "captions").length === 1 &&
      !captionSoloSet
    ) {
      //player.setCurrentCaptions(1);
      captionSoloSet = true;
    }
  }, [jwPlaylist]);

  const onPause = useCallback(() => {
    dispatch(SetPlayingState(false));
  }, []);

  const onDuration = useCallback(
    (duration: number) => {
      if (!playerData) return;
      const frameDifferential = duration / playerData.durationInSeconds;
      dispatch(SetFrameDifferential(frameDifferential));
    },
    [playerData]
  );

  const loadAudioTracks = useCallback(() => {
    if (!_player.current) return;
    const internal = _player.current.getInternalPlayer("hls") as Hls;
    if (!internal) return;
    const tracksData = internal.audioTracks.map((x) => ({ ...x }));
    if (!tracksData) return;
    const audioTracks = tracksData.reduce(function (acc: any[], x, index) {
      return [...acc, { language: x.lang, name: x.name }];
    }, []);
    dispatch(SetAudioTracks(audioTracks));
    dispatch(SetActiveLanguages({ activeCaptionIndex: null, activeAudioTrackIndex: internal.audioTrack as number }));
  }, []);

  const onReady = useCallback(
    (player: ReactPlayer) => {
      if (playerInput.objectId) mediaService.SetPlayLog(playerInput.objectId);
      setIsPlayerMounted(true);
      _player.current = player;

      const htmlVideo = player.getInternalPlayer() as HTMLVideoElement;

      if ("requestVideoFrameCallback" in HTMLVideoElement.prototype) {
        const updateVideoTime = (now, metadata) => {
          // Handle Safari's negative timestamp bug
          if (metadata.mediaTime < 0 || metadata.expectedDisplayTime < 0) {
            // Fallback to using the video's currentTime
            dispatch(SetCurrentFrame(htmlVideo.currentTime * playerData.frameRate));
          } else {
            dispatch(SetCurrentFrame(metadata.mediaTime * playerData.frameRate));
          }
          htmlVideo.requestVideoFrameCallback(updateVideoTime);
        };

        htmlVideo.requestVideoFrameCallback(updateVideoTime);
      }
    },
    [playerInput.objectId, playerData]
  );

  const onEnded = useCallback(() => {
    if (cacheService.GetPlatformMode() !== PlatformMode.Adam) dispatch(PlayPause());
    else setShowComplete(true);
  }, []);

  const tracks = useMemo(() => {
    return (
      jwPlaylist[0].tracks?.map((x, i) => ({
        kind: x.kind,
        label: x.label ?? "",
        src: x.file,
        srcLang: x.label ?? "",
      })) ?? null
    );
  }, [jwPlaylist, jwPlaylist && jwPlaylist[0] && jwPlaylist[0].tracks && jwPlaylist[0].tracks.length]);

  useEffect(() => {
    if (
      (loadedKey !== playerInput.objectId && playerInput.shareMediaKey !== loadedKey) ||
      playerInput.episode !== loadedEpisode
    ) {
      resetPlayerState(playerInput.objectId ?? playerInput.shareMediaKey, playerInput.episode);
      _player.current = null;
    }
  }, [playerInput.objectId, playerInput.shareMediaKey, playerInput.episode]);

  /// DF-4327 Prevent concurrency loading with previous player setting on another instance.
  useEffect(() => {
    return () => {
      resetPlayerState(null);
      dispatch(UnsetInput());
    };
  }, []);

  useEffect(() => {
    if (_player.current !== null && activeAudioTrackIndex !== null && isPlayerMounted) {
      const internal = _player.current.getInternalPlayer("hls") as Hls;
      if (internal) internal.audioTrack = activeAudioTrackIndex;
    }
  }, [activeAudioTrackIndex, isPlayerMounted]);

  // Handle seek
  useEffect(() => {
    if (playerSeek.frame >= 0 && _player.current !== null && isPlayerMounted) {
      const framesToSeek = playerSeek.frame - currentFrame;
      _player.current.seekTo(_player.current.getCurrentTime() + framesToSeek / playerData.frameRate, "seconds");
    }
  }, [playerSeek]);

  // Handle Play Pause
  useEffect(handlePlayPause, [playerConf.playPause]);

  // Handle ttml captions
  useEffect(() => {
    if (_player.current !== null && activeCaptionIndex !== null && isPlayerMounted) {
      const video = document.querySelector("video");
      if (!video) return;
      for (let i = 0; i < video.textTracks.length; i++) {
        video.textTracks[i].mode = i === activeCaptionIndex ? "showing" : "hidden";
        if (activeTrackIndexRef.current === i) {
          cuesRef.current.forEach((c) => {
            video.textTracks[i].removeCue(c);
          });
          clearSubFromScreen();
          cuesRef.current = [];
        }
      }
      const tracks = video.getElementsByTagName("track");
      if (tracks[activeCaptionIndex] && tracks[activeCaptionIndex].src) {
        if (!isVtt(tracks[activeCaptionIndex].src))
          fetch(tracks[activeCaptionIndex].src).then(async (res) => {
            const text = await res.text();
            initTrack(text, activeCaptionIndex);
          });
      }
    }
  }, [activeCaptionIndex]);

  // Handle dimensions change
  useEffect(() => {
    if (context === PreviewContext.DistribEnlarged || context === PreviewContext.DriveEnlarged) {
      dispatch(SetPlayerHeight(containerHeight));
      dispatch(SetPlayerResponsive(false));
    }

    return () => {
      if (context === PreviewContext.DistribEnlarged || context === PreviewContext.DistribEnlargedTabs) {
        dispatch(SetPlayerHeight(null));
        dispatch(SetPlayerResponsive(true));
      }
    };
  }, [containerHeight, context]);

  useEffect(() => {
    loadAudioTracks();
  }, [isPlayerMounted]);

  useEffect(() => {
    if (playerDiv.current !== null) {
      // For FS unsupported on non-video elements (iOS Safari)
      const video = playerDiv.current.querySelector("video");

      // Leave fullscreen
      if (!playerFullScreen) {
        if (document.fullscreenElement) {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
          } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
          }
        }
        if (video !== null) {
          video.removeEventListener("webkitendfullscreen", leaveHTMLVideoFullScreen);
        }
      } else {
        // Enter fullscreen
        if (playerDiv.current.requestFullscreen) {
          // W3C API
          playerDiv.current.requestFullscreen();
        } else if (playerDiv.current.mozRequestFullscreen) {
          // Mozilla current API
          playerDiv.current.mozRequestFullscreen();
        } else if (playerDiv.current.webkitEnterFullscreen) {
          // Webkit current API
          playerDiv.current.webkitEnterFullscreen();
        } else if (playerDiv.current.webkitRequestFullscreen) {
          //# for Safari (older versions)
          playerDiv.current.webkitRequestFullscreen();
        } else {
          // Fullscreen is not supported on non-Video Elements (iOS Safari)
          if (video !== null) {
            video.webkitEnterFullscreen();
            video.addEventListener("webkitendfullscreen", leaveHTMLVideoFullScreen);
          }
        }
      }
    }
  }, [playerFullScreen]);

  // Needed to listen to Escape default binding on browser
  useEffect(() => {
    function handleFullscreenChange() {
      dispatch(SetPlayerFullScreen(document.fullscreenElement !== null));
    }
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  return (
    <Box
      ref={playerDiv}
      onClick={(e: React.MouseEvent<HTMLDivElement>) => {
        if (!isBuffering && e.target instanceof HTMLVideoElement) dispatch(PlayPause());
      }}
      sx={(t) => ({
        position: "relative",
        cursor: idle && playerFullScreen ? "none" : "pointer",
        width: "100%",
        background: background,

        ...(!playerFullScreen &&
          !responsive && {
            height: playerHeight,
          }),
      })}
    >
      {/* Loading & Issues */}
      {!playerData?.smilFullUrl || jwPlaylist[0].file === "" ? (
        <Stack
          sx={(t) => ({
            width: "100%",
            aspectRatio: aspectRatio ?? "16/9",
            justifyContent: "center",
            alignItems: "center",
            background: t.palette.background.default,

            ...(!playerFullScreen &&
              !responsive && {
                height: playerHeight,
              }),
          })}
        >
          <Typography variant="h4">
            {!loading ? t("component:body.noProxyAvailableYet") : GlobalConst.LOADING_TEXT}
          </Typography>
          <Typography color="text.tier" variant="body1">
            {playerData?.message ?? ""}
          </Typography>
        </Stack>
      ) : (
        <>
          <PlayerDefaultOverlay hasFirstPlayed={hasFirstPlayed} context={context} hover={hoveringPlayer} />

          {/* <video> container */}
          <Box
            sx={{
              position: "relative",

              ...(responsive && {
                aspectRatio: aspectRatio ?? "16/9",
              }),
              width: "100%",

              ...(!playerFullScreen &&
                !responsive && {
                  height: `calc(${playerHeight}px - 50px)`,
                }),

              ...(playerFullScreen && {
                width: "100%",
                height: "100%",
              }),
            }}
          >
            {isBuffering && <PlayerBufferingOverlay />}
            <ReactPlayer
              width="100%"
              height="100%"
              url={playerData.smilFullUrl ?? ""}
              playing={isPlaying}
              onBuffer={onBuffer}
              onBufferEnd={onBufferEnd}
              onProgress={onProgress}
              onPlay={onPlay}
              onPause={onPause}
              onReady={onReady}
              onDuration={onDuration}
              onEnded={onEnded}
              onEnablePIP={handleEnablePip}
              onDisablePIP={handleLeavePip}
              playbackRate={playbackSpeed}
              volume={playerVolume / 100}
              pip={playerPip}
              config={{
                file: {
                  attributes: { crossOrigin: "anonymous" },
                  hlsOptions: {
                    lowLatencyMode: true,
                    backBufferLength: 90,
                    enableWorker: true,
                  },
                  tracks,
                } as FileConfig,
              }}
            />
            {assetType === ObjectType.Audio && !playerData.videoStream && <AudioOverlay />}
            {hasFirstPlayed && (
              <SubtitlesContainer fullscreen={playerFullScreen} hover={hoveringPlayer} ref={subtitlesList} />
            )}
          </Box>

          {/* Overlays */}
          <CompleteOverlay isComplete={showComplete} />

          {/* Timeline */}

          {/* Controls */}
          {playerFullScreen ? (
            <Collapse
              timeout={500}
              in={!idle}
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                background: "linear-gradient(to top, rgba(0,0,0,0.5), transparent)",
              }}
            >
              <PlayerProgress type={ProgressBarType.Player} context={context} />
              <PlayerControls assetType={assetType} context={context} />
            </Collapse>
          ) : (
            <Box
              sx={(t) => ({
                opacity: hasFirstPlayed ? 1 : 0,
                ...theme.helpers(theme).baseTransition,
                position: "relative",
              })}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  transform: "translateY(-100%)",
                }}
              >
                <PlayerTimeline />
              </Box>
              {isCreatingTimelineEvent ? (
                <PlayerTimelinePlaceholder hasFirstPlayed={hasFirstPlayed} size="small" />
              ) : (
                <PlayerProgress type={ProgressBarType.Player} context={context} />
              )}
              <PlayerControls assetType={assetType} context={context} />
            </Box>
          )}

          {props.children}
        </>
      )}
    </Box>
  );
};

export default ReactPlayerWrapper;

const MediaPlayer = styled(Box)(({ theme }) => ({
  flex: 1,
  borderRadius: 6,
  overflow: "hidden",
  width: `100%`,
  height: `100%`,
  textAlign: "center",
  video: {
    width: "100%",
    height: "100%",
  },
}));

const SubtitlesContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "hover" && prop !== "fullscreen",
})<{ hover: boolean; fullscreen: boolean }>(({ theme, hover, fullscreen }) => ({
  pointerEvents: "none",
  width: "100%",
  height: "100%",
  position: "absolute",
  bottom: 0,
  left: 0,
  textShadow:
    "rgb(0, 0, 0) -2px 0px 1px, rgb(0, 0, 0) 2px 0px 1px, rgb(0, 0, 0) 0px -2px 1px, rgb(0, 0, 0) 0px 2px 1px, rgb(0, 0, 0) -1px 1px 1px, rgb(0, 0, 0) 1px 1px 1px, rgb(0, 0, 0) 1px -1px 1px, rgb(0, 0, 0) 1px 1px 1px",
  "p > span": {
    backgroundColor: "rgba(0,0,0,.5)",
  },
  fontSize: "16px",
}));

export const ReactPlayerWrapperStyled = {
  MediaPlayer,
};
